
import { useState, useEffect } from "react";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { format, parse, addMinutes, isPast, startOfDay } from "date-fns";
import { pt, enUS } from "date-fns/locale";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Clock, AlertTriangle, Video, Globe, Users, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Check } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";

type EventType = "training_session" | "assessment" | "other" | "online_session" | "group_class";
type EventStatus = "scheduled" | "completed" | "cancelled";
interface Student {
  id: string;
  first_name: string;
  last_name: string;
}
interface EventDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedEvent?: any;
  selectedDate?: Date;
  onEventSaved?: () => void;
  onEventDeleted?: (event: any) => void;
  isDeleting?: boolean;
}
interface EventData {
  title: string;
  event_type: EventType;
  start_time: string;
  end_time: string;
  trainer_id: string;
  student_id: string | null;
  status: EventStatus;
  is_online?: boolean;
  meeting_url?: string;
  is_video?: boolean;
  video_url?: string;
}

export const EventDialog = ({
  isOpen,
  onClose,
  selectedEvent,
  selectedDate,
  onEventSaved,
  onEventDeleted,
  isDeleting = false
}: EventDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState(selectedEvent?.title || "");
  const [eventType, setEventType] = useState<EventType>(selectedEvent?.event_type || "training_session");
  const [duration, setDuration] = useState("60");
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<string | null>(null);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  const [date, setDate] = useState<Date | undefined>(selectedDate);
  const [time, setTime] = useState<string>(selectedDate ? format(selectedDate, "HH:mm") : "09:00");
  const [isOnline, setIsOnline] = useState<boolean>(selectedEvent?.is_online || false);
  const [meetingUrl, setMeetingUrl] = useState<string>(selectedEvent?.meeting_url || "");
  const [isVideo, setIsVideo] = useState<boolean>(selectedEvent?.is_video || false);
  const [videoUrl, setVideoUrl] = useState<string>(selectedEvent?.video_url || "");

  const { user } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslations("schedule");

  const isDateInPast = date ? isPast(new Date(date.setHours(parseInt(time.split(':')[0]), parseInt(time.split(':')[1]), 0, 0))) : false;
  const dateLocale = language === 'pt' ? pt : enUS;

  useEffect(() => {
    if (selectedDate) {
      setDate(selectedDate);
      setTime(format(selectedDate, "HH:mm"));
    }
  }, [selectedDate]);

  useEffect(() => {
    const fetchStudents = async () => {
      if (!user?.id) return;
      try {
        const { data, error } = await supabase
          .from('students')
          .select('id, first_name, last_name')
          .eq('trainer_id', user.id)
          .eq('status', 'active');
        if (error) throw error;
        setStudents(data || []);
      } catch (_error) {
        // Silently handle - students list will just be empty
      }
    };
    fetchStudents();
  }, [user?.id, t]);

  useEffect(() => {
    if (eventType !== 'other') {
      const titles: Record<string, string> = {
        training_session: t("eventDialog.class"),
        assessment: t("eventDialog.assessment"),
        online_session: t("eventDialog.online_class"),
        group_class: t("eventDialog.group_class"),
        other: ""
      };
      setTitle(titles[eventType] || "");
    }
    
    if (eventType === 'online_session') {
      setIsOnline(true);
    } else if (eventType !== 'other') {
      setIsOnline(false);
    }
    
    if (eventType !== 'training_session' && eventType !== 'online_session' && eventType !== 'group_class') {
      setIsVideo(false);
      setVideoUrl('');
    }

    // Clear single student when switching to group
    if (eventType === 'group_class') {
      setSelectedStudent(null);
    } else {
      setSelectedParticipants([]);
    }
  }, [eventType, t]);

  const toggleParticipant = (studentId: string) => {
    setSelectedParticipants(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSaveEvent = async () => {
    if (!date || !user?.id) {
      toast.error(t("eventDialog.selectTimeError"));
      return;
    }
    
    if (eventType === 'group_class') {
      if (selectedParticipants.length === 0) {
        toast.error(t("eventDialog.selectParticipantsError"));
        return;
      }
    } else if ((eventType === 'training_session' || eventType === 'assessment' || eventType === 'online_session') && !selectedStudent) {
      toast.error(t("eventDialog.selectStudentError"));
      return;
    }

    if (isOnline && !meetingUrl && !isVideo) {
      toast.error(language === 'pt' ? "Por favor, insira o URL da reunião online" : "Please enter the online meeting URL");
      return;
    }

    if (isVideo && !videoUrl) {
      toast.error(language === 'pt' ? "Por favor, insira o URL do vídeo" : "Please enter the video URL");
      return;
    }

    if (isDateInPast) {
      toast.error(t("eventDialog.pastDateError"));
      return;
    }
    
    setLoading(true);
    try {
      const hours = parseInt(time.split(':')[0]);
      const minutes = parseInt(time.split(':')[1]);
      const startDateTime = new Date(date);
      startDateTime.setHours(hours, minutes, 0, 0);
      const endDateTime = addMinutes(startDateTime, parseInt(duration));
      
      const finalEventType = isOnline && eventType !== 'group_class' ? 'online_session' : eventType;
      
      const eventData: EventData = {
        title,
        event_type: finalEventType as any,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        trainer_id: user.id,
        student_id: eventType === 'group_class' ? null : selectedStudent,
        status: "scheduled",
        is_online: isOnline,
        meeting_url: isOnline ? meetingUrl : null,
        is_video: isVideo,
        video_url: isVideo ? videoUrl : null
      };
      
      const { data: eventResult, error } = await supabase
        .from('schedule_events')
        .insert(eventData)
        .select('id')
        .single();
      if (error) throw error;
      
      // If group class, insert participants
      if (eventType === 'group_class' && eventResult?.id && selectedParticipants.length > 0) {
        const participants = selectedParticipants.map(studentId => ({
          event_id: eventResult.id,
          student_id: studentId
        }));
        
        const { error: participantsError } = await supabase
          .from('group_class_participants')
          .insert(participants);
        
        if (participantsError) {
          console.error('Error adding participants:', participantsError);
          // Event was created, just warn about participants
          toast.warning(language === 'pt' ? "Evento criado mas houve erro ao adicionar participantes" : "Event created but error adding participants");
        }
      }
      
      toast.success(t("eventDialog.eventCreated"));
      onClose();
      onEventSaved?.();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error(t("eventDialog.eventCreateError"));
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async () => {
    if (!selectedEvent || !user?.id) return;
    setLoading(true);
    try {
      const { error } = await supabase.from('schedule_requests').update({ status: 'approved' }).eq('id', selectedEvent.id);
      if (error) throw error;
      toast.success(t("eventDialog.requestApproved"));
      onClose();
      onEventSaved?.();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(t("eventDialog.approveError"));
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedEvent || !user?.id) return;
    setLoading(true);
    try {
      const { error: requestError } = await supabase.from('schedule_requests').update({ status: 'rejected' }).eq('id', selectedEvent.id);
      if (requestError) throw requestError;

      const { data: relatedEvents } = await supabase
        .from('schedule_events')
        .select('*')
        .eq('student_id', selectedEvent.student_id)
        .eq('start_time', selectedEvent.start_time || selectedEvent.requested_date);
      if (relatedEvents && relatedEvents.length > 0) {
        const { error: deleteError } = await supabase
          .from('schedule_events')
          .delete()
          .in('id', relatedEvents.map(e => e.id));
        if (deleteError) throw deleteError;
      }
      toast.success(t("eventDialog.requestRejected"));
      onClose();
      onEventSaved?.();
      if (onEventDeleted) onEventDeleted(selectedEvent);
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(t("eventDialog.rejectError"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent || !onEventDeleted) return;
    onEventDeleted(selectedEvent);
  };

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const formattedHour = hour.toString().padStart(2, '0');
        const formattedMinute = minute.toString().padStart(2, '0');
        options.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    return options;
  };

  const getEventTitle = () => {
    if (selectedEvent?.event_type === 'class_request') return t("eventDialog.classRequest");
    return selectedEvent ? t("eventDialog.editEvent") : t("eventDialog.newEvent");
  };

  const renderEventActions = () => {
    if (selectedEvent?.event_type === 'class_request') {
      return (
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="destructive" onClick={handleRejectRequest} disabled={loading}>
            <X className="h-4 w-4 mr-2" />
            {t("eventDialog.rejectButton")}
          </Button>
          <Button onClick={handleApproveRequest} disabled={loading} className="bg-green-600 hover:bg-green-700">
            <Check className="h-4 w-4 mr-2" />
            {t("eventDialog.approveButton")}
          </Button>
        </div>
      );
    }
    return (
      <div className="flex justify-end gap-2 mt-4">
        {selectedEvent && (
          <Button variant="destructive" onClick={handleDeleteEvent} disabled={isDeleting}>
            {isDeleting ? t("eventDialog.deleting") : t("eventDialog.deleteButton")}
          </Button>
        )}
        <Button variant="outline" onClick={onClose}>{t("eventDialog.cancelButton")}</Button>
        {!selectedEvent && (
          <Button onClick={handleSaveEvent} disabled={loading || isDateInPast}>
            {loading ? t("eventDialog.saving") : t("eventDialog.saveButton")}
          </Button>
        )}
      </div>
    );
  };

  const needsStudentSelect = eventType === 'training_session' || eventType === 'assessment' || eventType === 'online_session';
  const needsParticipantSelect = eventType === 'group_class';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="dropdown-opaque max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getEventTitle()}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {selectedEvent ? (
            <>
              <div>
                <span className="font-medium">{t("eventDialog.date")}:</span>{" "}
                {format(new Date(selectedEvent.start_time || selectedEvent.requested_date), "dd 'de' MMMM 'às' HH:mm", { locale: dateLocale })}
              </div>
              {selectedEvent.students && (
                <div>
                  <span className="font-medium">{t("eventDialog.student")}:</span>{" "}
                  {selectedEvent.students.first_name} {selectedEvent.students.last_name}
                </div>
              )}
              {selectedEvent.student && (
                <div>
                  <span className="font-medium">{t("eventDialog.student")}:</span>{" "}
                  {selectedEvent.student.first_name} {selectedEvent.student.last_name}
                </div>
              )}
              {selectedEvent.is_online && selectedEvent.meeting_url && (
                <div>
                  <span className="font-medium">{language === 'pt' ? "Link da Aula Online" : "Online Class Link"}:</span>{" "}
                  <a href={selectedEvent.meeting_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{selectedEvent.meeting_url}</a>
                </div>
              )}
              {selectedEvent.is_video && selectedEvent.video_url && (
                <div>
                  <span className="font-medium">{language === 'pt' ? "Link do Vídeo" : "Video Link"}:</span>{" "}
                  <a href={selectedEvent.video_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{selectedEvent.video_url}</a>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Date & Time */}
              <div className="space-y-2">
                <Label htmlFor="date">{t("eventDialog.date")}</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant={"outline"} className={cn("w-full sm:w-[240px] justify-start text-left font-normal", !date && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "dd 'de' MMMM 'de' yyyy", { locale: dateLocale }) : <span>{t("eventDialog.selectDate")}</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate} initialFocus className="p-3 pointer-events-auto bg-white" disabled={date => isPast(startOfDay(date))} />
                    </PopoverContent>
                  </Popover>
                  
                  <Select value={time} onValueChange={setTime}>
                    <SelectTrigger className="w-full sm:w-[150px]">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-2" />
                        <SelectValue placeholder={t("eventDialog.selectTime")} />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="dropdown-opaque">
                      <div className="max-h-[300px] overflow-y-auto">
                        {generateTimeOptions().map(timeOption => (
                          <SelectItem key={timeOption} value={timeOption}>{timeOption}</SelectItem>
                        ))}
                      </div>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {isDateInPast && (
                <Alert variant="warning">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{t("eventDialog.pastDateWarning")}</AlertDescription>
                </Alert>
              )}

              {/* Event Type */}
              <div className="space-y-2">
                <Label htmlFor="eventType">{t("eventDialog.eventType")}</Label>
                <Select value={eventType} onValueChange={(value: EventType) => setEventType(value)}>
                  <SelectTrigger id="eventType">
                    <SelectValue placeholder={t("eventDialog.selectType")} />
                  </SelectTrigger>
                  <SelectContent className="dropdown-opaque">
                    <SelectItem value="training_session">{t("eventDialog.class")}</SelectItem>
                    <SelectItem value="online_session">{t("eventDialog.online_class")}</SelectItem>
                    <SelectItem value="group_class">
                      <span className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {t("eventDialog.group_class")}
                      </span>
                    </SelectItem>
                    <SelectItem value="assessment">{t("eventDialog.assessment")}</SelectItem>
                    <SelectItem value="other">{t("eventDialog.other")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Title for "other" type */}
              {eventType === 'other' && (
                <div className="space-y-2">
                  <Label htmlFor="title">{t("eventDialog.title")}</Label>
                  <Input id="title" value={title} onChange={e => setTitle(e.target.value)} placeholder={t("eventDialog.title")} />
                </div>
              )}

              {/* Single student select */}
              {needsStudentSelect && (
                <div className="space-y-2">
                  <Label htmlFor="student">{t("eventDialog.student")}</Label>
                  <Select value={selectedStudent || ""} onValueChange={setSelectedStudent}>
                    <SelectTrigger id="student">
                      <SelectValue placeholder={t("eventDialog.selectStudent")} />
                    </SelectTrigger>
                    <SelectContent className="dropdown-opaque">
                      {students.map(student => (
                        <SelectItem key={student.id} value={student.id}>
                          {student.first_name} {student.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Multi-select participants for group class */}
              {needsParticipantSelect && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {t("eventDialog.participants")}
                  </Label>
                  
                  {/* Selected participants badges */}
                  {selectedParticipants.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {selectedParticipants.map(id => {
                        const student = students.find(s => s.id === id);
                        if (!student) return null;
                        return (
                          <Badge key={id} variant="secondary" className="flex items-center gap-1 pr-1">
                            {student.first_name} {student.last_name}
                            <button 
                              onClick={() => toggleParticipant(id)}
                              className="ml-1 rounded-full hover:bg-muted p-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  
                  <ScrollArea className="h-[180px] border rounded-md p-2">
                    <div className="space-y-1">
                      {students.map(student => (
                        <label
                          key={student.id}
                          className={cn(
                            "flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors",
                            selectedParticipants.includes(student.id) 
                              ? "bg-primary/10 dark:bg-primary/20" 
                              : "hover:bg-muted/50"
                          )}
                        >
                          <Checkbox
                            checked={selectedParticipants.includes(student.id)}
                            onCheckedChange={() => toggleParticipant(student.id)}
                          />
                          <span className="text-sm">
                            {student.first_name} {student.last_name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </ScrollArea>
                  <p className="text-xs text-muted-foreground">
                    {selectedParticipants.length} {t("eventDialog.participantsCount")}
                  </p>
                </div>
              )}

              {/* Online/Video options */}
              {(eventType === 'training_session' || eventType === 'online_session' || eventType === 'group_class') && (
                <div className="space-y-4">
                  {(eventType === 'training_session' || eventType === 'group_class') && (
                    <div className="flex items-center space-x-2">
                      <Label htmlFor="isOnline">{language === 'pt' ? "É uma aula online?" : "Is this an online class?"}</Label>
                      <Switch id="isOnline" checked={isOnline} onCheckedChange={setIsOnline} />
                      {isOnline && <Globe className="h-4 w-4 text-blue-500" />}
                    </div>
                  )}
                  
                  {isOnline && (
                    <div className="space-y-2">
                      <Label htmlFor="meetingUrl">{language === 'pt' ? "Link da Reunião (Zoom, Google Meet, etc.)" : "Meeting URL (Zoom, Google Meet, etc.)"}</Label>
                      <Input id="meetingUrl" value={meetingUrl} onChange={e => setMeetingUrl(e.target.value)} placeholder="https://zoom.us/j/123456789" className="bg-white dark:bg-gray-800" />
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="isVideo">{language === 'pt' ? "Incluir vídeo pré-gravado?" : "Include pre-recorded video?"}</Label>
                    <Switch id="isVideo" checked={isVideo} onCheckedChange={setIsVideo} />
                    {isVideo && <Video className="h-4 w-4 text-red-500" />}
                  </div>
                  
                  {isVideo && (
                    <div className="space-y-2">
                      <Label htmlFor="videoUrl">{language === 'pt' ? "Link do Vídeo" : "Video URL"}</Label>
                      <Input id="videoUrl" value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="https://youtu.be/video123" className="bg-white dark:bg-gray-800" />
                    </div>
                  )}
                </div>
              )}

              {/* Duration */}
              <div className="space-y-2">
                <Label htmlFor="duration">{t("eventDialog.duration")}</Label>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger id="duration">
                    <SelectValue placeholder={t("eventDialog.selectDuration")} />
                  </SelectTrigger>
                  <SelectContent className="dropdown-opaque">
                    <SelectItem value="30">{t("eventDialog.30min")}</SelectItem>
                    <SelectItem value="45">{t("eventDialog.45min")}</SelectItem>
                    <SelectItem value="60">{t("eventDialog.1hour")}</SelectItem>
                    <SelectItem value="90">{t("eventDialog.90min")}</SelectItem>
                    <SelectItem value="120">{t("eventDialog.2hours")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {renderEventActions()}
        </div>
      </DialogContent>
    </Dialog>
  );
};
